import { Injectable, inject } from '@angular/core';

import { CookieName, CookieService, DateTimeService, UserEvent, UserLoginEvent, UserLogoutEvent, UserService } from '@frontend/vanilla/core';
import { toNumber } from 'lodash-es';
import { first } from 'rxjs';

/** @stable */
@Injectable({
    providedIn: 'root',
})
export class AccountMenuOnboardingService {
    private readonly cookieService = inject(CookieService);
    private readonly dateTimeService = inject(DateTimeService);
    private readonly user = inject(UserService);

    loginCount: number;
    tourStartedLoginCount: number | undefined;
    tourDismissed: boolean;

    get enabled(): boolean {
        return this._enabled;
    }

    get isFirstLogin(): boolean {
        return this.enabled && this.loginCount === 1;
    }

    private get oneYearFromNow(): Date {
        const oneYearFromNow = this.dateTimeService.now();
        oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

        return oneYearFromNow;
    }

    private _enabled: boolean;

    init(enabled: boolean) {
        if (this._enabled) {
            return;
        }

        this._enabled = enabled;
        this.loginCount = this.getLoginCount();
        this.tourStartedLoginCount = this.getTourStartedLoginCount();
        this.tourDismissed = this.getTourDismissed();

        if (this.user.isAuthenticated && !this.cookieService.get(CookieName.VnLogin)) {
            if (!this._enabled) return;
            this.incrementLoginCount();
            this.incrementTourStartedLoginCount();
            this.cookieService.put(CookieName.VnLogin, '1');
        }

        this.user.events.pipe(first((e: UserEvent) => e instanceof UserLoginEvent)).subscribe(() => {
            if (!this._enabled) return;
            this.incrementLoginCount();
            this.incrementTourStartedLoginCount();
            this.cookieService.put(CookieName.VnLogin, '1');
        });

        this.user.events.pipe(first((e: UserEvent) => e instanceof UserLogoutEvent)).subscribe(() => {
            this.cookieService.remove(CookieName.VnLogin);
        });
    }

    saveTourStartedLoginCount(count: number) {
        this.cookieService.put(CookieName.VnOtslc, count.toString(), { expires: this.oneYearFromNow });
        this.tourStartedLoginCount = count;
    }

    saveTourCompleted() {
        this.cookieService.put(CookieName.VnOtc, '1', { expires: this.oneYearFromNow });
    }

    saveTourDismissed() {
        this.cookieService.put(CookieName.VnOtd, '1', { expires: this.oneYearFromNow });
        this.tourDismissed = true;
    }

    private getLoginCount(): number {
        const loginCount = this.cookieService.get(CookieName.VnOlc) ?? '0';

        return toNumber(loginCount);
    }

    private getTourStartedLoginCount(): number | undefined {
        const count = this.cookieService.get(CookieName.VnOtslc);

        return count === undefined ? undefined : toNumber(count);
    }

    private getTourDismissed(): boolean {
        const tourDismissed = this.cookieService.get(CookieName.VnOtd);
        return tourDismissed === '1';
    }

    private incrementLoginCount() {
        this.loginCount = this.getLoginCount();
        this.loginCount++;
        this.cookieService.put(CookieName.VnOlc, this.loginCount.toString(), { expires: this.oneYearFromNow });
    }

    private incrementTourStartedLoginCount() {
        this.tourStartedLoginCount = this.getTourStartedLoginCount();
        if (this.tourStartedLoginCount != undefined) {
            this.saveTourStartedLoginCount(this.tourStartedLoginCount + 1);
            this.tourStartedLoginCount++;
        }
    }
}
